/**
 * CER portal circuit-breaker size.
 *
 * Both Hardware inputs (`Circuit_Breaker_Size_Main_Switch_Grid` and
 * `Circuit_Breaker_Size_Main_Switch_Inverter_Alternative`) only accept
 * `^(\d+|(\d*\.\d{1,2}))$` — digits, or a decimal with 1–2 places. A unit
 * such as "63A" fails portal validation.
 *
 * Normalise what an operator actually types. Anything that is not a number
 * with at most two decimal places is rejected so the fill can flag it
 * instead of posting a value the portal will refuse.
 */
const PORTAL_CIRCUIT_BREAKER = /^(?:\d+|\d*\.\d{1,2})$/;
const UNIT = /^(?:amperes?|amps?|a)$/i;
const REJECT = 'Enter a number with up to 2 decimal places, such as 63 or 63.5.';
/** Blank input clears the field. A successful value is portal-legal. */
export function normalizeCircuitBreakerSize(raw) {
    const compact = raw.trim().replace(/,/g, '');
    if (!compact)
        return { ok: true, value: '' };
    const parts = compact.split(/\s+/);
    let body = compact;
    if (parts.length === 2 && UNIT.test(parts[1] ?? '')) {
        body = parts[0] ?? '';
    }
    else if (parts.length === 1) {
        const stuck = /^(\d*\.?\d+)(amperes?|amps?|a)$/i.exec(compact);
        if (stuck?.[1])
            body = stuck[1];
    }
    else {
        return { ok: false, reason: REJECT };
    }
    if (/^\d+\.$/.test(body))
        body = body.slice(0, -1);
    const decimal = /^(\d*)\.(\d+)$/.exec(body);
    if (decimal && (decimal[2] ?? '').length > 2) {
        const frac = (decimal[2] ?? '').replace(/0+$/, '');
        if (frac.length > 2)
            return { ok: false, reason: REJECT };
        const whole = decimal[1] ?? '';
        body = frac.length === 0 ? whole || '0' : `${whole}.${frac}`;
    }
    if (!PORTAL_CIRCUIT_BREAKER.test(body))
        return { ok: false, reason: REJECT };
    return { ok: true, value: body };
}
/** The two job-level breaker leaves. Kept as text; compared numerically. */
export const CIRCUIT_BREAKER_FIELDS = [
    'circuitBreakerSizeMainSwitchGrid',
    'circuitBreakerSizeMainSwitchInverter',
];
export function circuitBreakersEquivalent(a, b) {
    if (typeof a !== 'string' || typeof b !== 'string')
        return undefined;
    const left = normalizeCircuitBreakerSize(a);
    const right = normalizeCircuitBreakerSize(b);
    if (!left.ok || !right.ok || !left.value || !right.value)
        return undefined;
    return Number(left.value) === Number(right.value);
}
/** Store a portal-legal size. Invalid text is left unchanged so fill can flag it. */
export function storedCircuitBreakerSize(raw) {
    if (raw === undefined)
        return undefined;
    const normalized = normalizeCircuitBreakerSize(raw);
    if (!normalized.ok)
        return raw.trim() || undefined;
    return normalized.value || undefined;
}
//# sourceMappingURL=circuit-breaker-size.js.map